import React, { useCallback, useMemo } from "react";

import { CustomModal } from "../CustomModal";
import { GeneralErrorBoundary } from "../ErrorBoundary";
import DynamicChartMemoized from "./DynamicChartMemoized";

export const DynamicChartFullScreenHandler = ({ children, className, isFullScreen }) => {
  if (isFullScreen)
    return (
      <div className={className} style={{ height: "100vh", width: "100vw" }}>
        <CustomModal closeIcon={false} height="100vh" modalOpen={true} padding="0" width="100vw">
          {children}
        </CustomModal>
      </div>
    );
  else return children;
};

export const DynamicChart = ({
  allFilters,
  allSettings,
  chartSettingsDependencies,
  config,
  fullScreenChart,
  id,
  removeChart,
  removeChartIndividalFilterKey,
  specificDataGetter,
  updateChart,
  ...props
}) => {
  const fullScreenChartMemoized = useCallback(fullScreenChart, []);
  const updateChartMemoized = useCallback((key, value) => updateChart(key, value), []);
  const removeChartIndividalFilterKeyMemoized = useCallback((key) => removeChartIndividalFilterKey(key), []);

  const specificDataGetterMemoized = useCallback(specificDataGetter, []);

  // Pass this parameter to the DynamicChartMemoized only to make a rerender when changed.
  const userLanguage = localStorage.getItem("language");
  const configMemoized = useMemo(
    () => config,
    [
      JSON.stringify(
        Object.entries(config).filter(([key, _value]) => !["x", "y"].includes(key))
        // .map(([_key, value]) => value)
      ),
    ]
  );

  const itemFilters = useMemo(
    () =>
      allFilters(updateChartMemoized, config)?.map((filter) => {
        return {
          ...filter,
          config: filter.adjustConfig(config),
          setConfig: filter.setConfig,
        };
      }),
    [configMemoized, allFilters]
  );

  const itemSettings = useMemo(
    () =>
      allSettings(updateChartMemoized, config)?.map((setting) => {
        return {
          ...setting,
          config: setting.adjustConfig(config),
          setConfig: !setting.isInDiagramsPart ? setting.setConfig(updateChartMemoized) : undefined,
          setConfigFn: setting.isInDiagramsPart ? setting.adjustSetConfigFn : undefined,
        };
      }),
    [configMemoized, ...(chartSettingsDependencies ? chartSettingsDependencies : [])]
  );

  return (
    <GeneralErrorBoundary resetKeys={[config, props]}>
      <DynamicChartMemoized
        config={configMemoized}
        filters={itemFilters}
        id={id}
        onClose={removeChart}
        removeChartIndividalFilterKey={removeChartIndividalFilterKeyMemoized}
        setConfig={updateChartMemoized}
        setFullScreenChartId={fullScreenChartMemoized}
        settings={itemSettings}
        specificDataGetter={specificDataGetterMemoized}
        userLanguage={userLanguage}
        {...props}
      />
    </GeneralErrorBoundary>
  );
};
